import { RangeDate } from '@stores/index';
import { Customers } from '@constants/index';
import { format, isSameDay } from 'date-fns';
import { Cell } from 'exceljs';
import { VesselCiiRatings, PeriodFocByFuelType } from '@services/index';

export type ColumnMeta<TInput = unknown, TOutput = unknown> = {
  id: string;
  displayName: string;
  rootProp?: string;
  transform?: (value: TInput, roundingFactor?: number) => TOutput;
  cellProps?: Partial<Cell>;
};

/**
 * Class to build column metadata for fleet ratings export.
 * It includes vessel details, fuel consumption, and CII ratings.
 * It also provides methods to generate columns based on the data available.
 *
 * @class FleetRatingsColumnsBuilder
 * @property {VesselCiiRatings[]} vesselsCiiRatings - Array of vessel CII ratings data.
 * @property {boolean} isDefaultView - Indicates if the view is the default one.
 * @property {ColumnMeta[]} summaryColumns - Array of column metadata for the summary view.
 *
 * @constructor
 * @param {VesselCiiRatings[]} vesselsCiiRatings - Array of vessel CII ratings data.
 * @param {RangeDate} initialDate - The initial date range for the report.
 * @param {RangeDate} selectedDate - The selected date range for the report.
 * @param {Customers} [customerName] - The name of the customer (optional).
 *
 * @example
 * const columnsBuilder = new FleetRatingsColumnsBuilder(vesselsCiiRatings, initialDate, selectedDate, Customers.KLINE);
 * const summaryColumns = columnsBuilder.summaryColumns;
 * const dailyReportColumns = columnsBuilder.getDailyReportsColumns(periodFocByFuelType);
 *
 * @returns {FleetRatingsColumnsBuilder} An instance of the class with the summary columns ready for export.
 */
export class FleetRatingsColumnsBuilder {
  vesselsCiiRatings: VesselCiiRatings[];
  isDefaultView: boolean;
  readonly summaryColumns: ColumnMeta[];

  static readonly vesselColumns: ColumnMeta[] = [
    {
      id: 'vesselName',
      displayName: 'VESSEL',
    },
    {
      id: 'yearBuilt',
      displayName: 'YEAR BUILT',
      cellProps: {
        numFmt: '0',
      },
    },
    {
      id: 'vesselType',
      displayName: 'VESSEL TYPE',
    },
    {
      id: 'shipyard',
      displayName: 'SHIPYARD',
    },
    {
      id: 'maxCapacity',
      displayName: 'CAPACITY',
      cellProps: {
        numFmt: '0',
      },
    },
  ];

  static readonly categoryColumns: ColumnMeta[] = [
    {
      id: 'department',
      displayName: 'DEPARTMENT',
    },
    {
      id: 'segment',
      displayName: 'SEGMENT',
    },
  ];

  static readonly categoryColumnsKline: ColumnMeta[] = [
    {
      id: 'department',
      displayName: 'DEPARTMENT',
    },
    {
      id: 'segment',
      displayName: 'SERIES',
    },
  ];

  static readonly ytdFocColumn: ColumnMeta[] = [
    {
      id: 'periodFoc',
      displayName: 'YTD FOC TOTAL (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
  ];

  static readonly defaultPeriodColumns: ColumnMeta[] = [
    {
      id: 'periodCo2',
      displayName: 'YTD CO2 (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'periodDistanceSailed',
      displayName: 'YTD DIST. SAILED (NM)',
      cellProps: {
        numFmt: '0',
      },
    },
    {
      id: 'periodAverageSpeed',
      displayName: 'YTD AVG SPEED (KTS)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'periodCiiScore',
      displayName: 'YTD CII SCORE',
      cellProps: {
        numFmt: '0.00',
      },
    },
    {
      id: 'periodCiiRating',
      displayName: 'YTD CII RATING',
      cellProps: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
      },
    },
  ];

  static readonly periodSelectionFocColumn: ColumnMeta[] = [
    {
      id: 'periodFoc',
      displayName: 'PERIOD FOC TOTAL (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
  ];

  static readonly periodSelectionColumns: ColumnMeta[] = [
    {
      id: 'periodCo2',
      displayName: 'PERIOD CO2 (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'periodDistanceSailed',
      displayName: 'PERIOD DIST. SAILED (NM)',
      cellProps: {
        numFmt: '0',
      },
    },
    {
      id: 'periodAverageSpeed',
      displayName: 'PERIOD AVG SPEED (KTS)',
      cellProps: {
        numFmt: '0.0',
      },
    },
  ];

  static readonly periodCIISelectionColumns: ColumnMeta[] = [
    {
      id: 'periodCiiScore',
      displayName: 'PERIOD CII SCORE',
      cellProps: {
        numFmt: '0.00',
      },
    },
    {
      id: 'periodCiiRating',
      displayName: 'PERIOD CII RATING',
      cellProps: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
      },
    },
  ];

  static readonly startOfPeriodColumns: ColumnMeta[] = [
    {
      id: 'startOfPeriodCiiScore',
      displayName: 'START OF PERIOD SCORE',
      cellProps: {
        numFmt: '0.00',
      },
    },
    {
      id: 'startOfPeriodCiiRating',
      displayName: 'START OF PERIOD RATING',
      cellProps: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
      },
    },
  ];

  static readonly endOfPeriodColumns: ColumnMeta[] = [
    {
      id: 'endOfPeriodCiiScore',
      displayName: 'END OF PERIOD CII SCORE',
      cellProps: {
        numFmt: '0.00',
      },
    },
    {
      id: 'endOfPeriodCiiRating',
      displayName: 'END OF PERIOD CII RATING',
      cellProps: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
      },
    },
  ];

  static readonly forecastColumns: ColumnMeta[] = [
    {
      id: 'forecastedFoc',
      displayName: 'FOREC. EOY FOC (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'forecastedCo2',
      displayName: 'FOREC. EOY CO2 (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'forecastedDistanceSailed',
      displayName: 'FOREC. EOY DIST. SAILED (NM)',
      cellProps: {
        numFmt: '0',
      },
    },
    {
      id: 'forecastedCiiScore',
      displayName: 'FOREC. EOY CII SCORE',
      cellProps: {
        numFmt: '0.00',
      },
    },
    {
      id: 'forecastedCiiRating',
      displayName: 'FOREC. EOY CII RATING',
      cellProps: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
      },
    },
  ];

  static readonly dailyReportsColumns: ColumnMeta[] = [
    {
      id: 'reportDates',
      displayName: 'REPORT DATE',
      transform: (value) => format(new Date((value as number) * 1000), `yyyy-MM-dd'T'HH:mm:ss`),
    },
    {
      id: 'distancesSailed',
      displayName: 'DISTANCE SAILED (NM)',
      cellProps: {
        numFmt: '0',
      },
    },
    {
      id: 'averageSpeeds',
      displayName: 'AVERAGE SPEED (KTS)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'foc',
      displayName: 'FOC (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
    {
      id: 'co2',
      displayName: 'CO2 (MT)',
      cellProps: {
        numFmt: '0.0',
      },
    },
  ];

  static readonly Customers = Customers;

  /**
   * Creates an instance of FleetRatingsColumnsBuilder.
   * This constructor initializes the columns based on the provided vessel CII ratings,
   * initial date, selected date, and optionally the customer name.
   * It determines if the view is the default one based on the date selection.
   */
  constructor(
    vesselsCiiRatings: VesselCiiRatings[],
    initialDate: RangeDate,
    selectedDate: RangeDate,
    customerName?: Customers
  ) {
    const toDateIsDifferent = !isSameDay(selectedDate.to[0], initialDate.to[0]);
    const fromDateIsDifferent = !isSameDay(selectedDate.from[0], initialDate.from[0]);
    const isDefaultView = !(toDateIsDifferent || fromDateIsDifferent);
    this.vesselsCiiRatings = vesselsCiiRatings;
    this.isDefaultView = isDefaultView;

    const fuelColumns = this.getSummaryFuelColumns();
    const categoryColumnsCustom =
      customerName == Customers.KLINE
        ? FleetRatingsColumnsBuilder.categoryColumnsKline
        : FleetRatingsColumnsBuilder.categoryColumns;
    if (isDefaultView) {
      this.summaryColumns = [
        ...FleetRatingsColumnsBuilder.vesselColumns,
        ...categoryColumnsCustom,
        ...FleetRatingsColumnsBuilder.ytdFocColumn,
        ...fuelColumns,
        ...FleetRatingsColumnsBuilder.defaultPeriodColumns,
        ...FleetRatingsColumnsBuilder.forecastColumns,
      ];
    } else {
      this.summaryColumns = [
        ...FleetRatingsColumnsBuilder.vesselColumns,
        ...categoryColumnsCustom,
        ...FleetRatingsColumnsBuilder.periodSelectionFocColumn,
        ...fuelColumns,
        ...FleetRatingsColumnsBuilder.periodSelectionColumns,
        ...FleetRatingsColumnsBuilder.startOfPeriodColumns,
        ...FleetRatingsColumnsBuilder.endOfPeriodColumns,
        ...FleetRatingsColumnsBuilder.periodCIISelectionColumns,
      ];
    }
  }

  /**
   * Generates the summary fuel columns based on the available fuel types in the vessel CII ratings data.
   * It creates a column for each unique fuel type found in the data.
   *
   * @returns {ColumnMeta[]} - An array of column metadata for the fuel types.
   */
  protected getSummaryFuelColumns = () => {
    const fuelTypesInData = new Set<string>();
    this.vesselsCiiRatings.forEach(({ periodFocByFuelType }) => {
      if (periodFocByFuelType) {
        Object.keys(periodFocByFuelType).forEach((fuel: string) => fuelTypesInData.add(fuel));
      }
    });

    return Array.from(fuelTypesInData).map((fuelType) => ({
      id: fuelType,
      rootProp: 'periodFocByFuelType',
      displayName: `${this.isDefaultView ? 'YTD' : 'PERIOD'} FOC ${fuelType} (MT)`,
      cellProps: {
        numFmt: '0.0',
      },
    }));
  };

  /**
   * Generates the columns for the daily reports based on the provided fuel consumption data.
   * It includes standard columns and additional columns for each fuel type present in the data.
   *
   * @param {PeriodFocByFuelType} periodFocByFuelType - The fuel consumption data categorized by fuel type.
   *
   * @returns {ColumnMeta[]} - An array of column metadata for the daily reports.
   */
  protected getDailyReportsColumns(periodFocByFuelType: PeriodFocByFuelType) {
    let fuelColumns: ColumnMeta[] = [];
    if (periodFocByFuelType) {
      fuelColumns = Object.keys(periodFocByFuelType).reduce((acum, curr) => {
        acum.push({
          id: curr,
          rootProp: 'focByFuelType',
          displayName: `FOC ${curr} (MT)`,
          cellProps: {
            numFmt: '0.0',
          },
        });
        return acum;
      }, [] as ColumnMeta[]);
    }
    return [...(FleetRatingsColumnsBuilder.dailyReportsColumns as ColumnMeta[]), ...fuelColumns];
  }
}
